import type { ScreenType } from '@/types/custom';
import { fixedPresetConfig } from '@/types/global-style';
import type { Control, ControlUI, GroupTypeSetting } from './types';
export const defaultSupportDevices: ScreenType[] = ['desktop', 'tablet', 'mobile']; // Sort flow media-max
export const defaultSupportPages = [
  {
    id: 'INDEX',
    label: 'Home Page',
    params: {
      pageType: 'index',
    },
  },
  {
    id: 'PRODUCT',
    label: 'Product Page',
    params: {
      pageType: 'product',
    },
  },
  {
    id: 'COLLECTION',
    label: 'Collection Page',
    params: {
      pageType: 'collection',
    },
  },
  {
    id: 'CART',
    label: 'Cart Page',
    params: {
      pageType: 'cart',
    },
  },
  {
    id: 'BLOG',
    label: 'Blog Page',
    params: {
      pageType: 'blog',
    },
  },
  {
    id: 'ARTICLE',
    label: 'Article Page',
    params: {
      pageType: 'article',
    },
  },
  {
    id: 'PAGE',
    label: 'Static Page',
    params: {
      pageType: 'page',
    },
  },
];

export const ROOT_TAG = 'ROOT';
export const MODAL_TAG = 'Dialog';
export const ROW_TAG = 'Row';
export const SECTION_TAG = 'Section';
export const COL_TAG = 'Col';
export const ADD_SECTION_IMAGE_TO_LAYOUT_ID = 'gps-add-section-image-to-layout-of-bottom';

export const defaultSupportContents = [
  {
    id: 'PRODUCT',
    to: 'ProductContent',
    label: 'Products',
  },
  {
    id: 'COLLECTION',
    to: 'CollectionContent',
    label: 'Collections',
  },
  {
    id: 'BLOG',
    to: 'BlogContent',
    label: 'Blogs',
  },
  {
    id: 'ARTICLE',
    to: 'ArticleContent',
    label: 'Articles',
  },
];

export const advancedSetting: { id: GroupTypeSetting; controls: Control[] } = {
  id: 'advanced',
  controls: [
    {
      id: 'd',
      label: 'Visibility',
      option: {
        label: 'large',
      },
      type: 'visibility',
      hideOnMode: {
        mobileOnly: true,
      },
      devices: {
        desktop: {
          default: true,
        },
        tablet: {
          default: true,
        },
        mobile: {
          default: true,
        },
      },
    },
    {
      id: 'adv-spacing',
      type: 'group',
      label: 'Spacing',
      option: {
        label: 'large',
      },
      controls: [
        {
          id: 'spacing-setting',
          label: 'Spacing',
          option: {
            label: 'medium',
          },
          type: 'spacing',
          devices: {
            desktop: {},
          },
        },
      ],
    },
    {
      type: 'group',
      label: 'Border',
      option: {
        label: 'large',
      },
      id: 'behavior-state',
      controls: [
        {
          id: 'border',
          type: 'border',
          label: 'Border',
          option: {
            label: 'medium',
          },
          default: {
            borderType: 'none',
            border: 'solid',
            borderWidth: '0px',
            width: '0px',
            color: '#121212',
            isCustom: true,
          },
        },
        {
          id: 'rounded',
          type: 'radius-preset',
          label: 'Corner',
          option: {
            label: 'medium',
          },
          default: {
            btrr: '0px',
            bblr: '0px',
            bbrr: '0px',
            btlr: '0px',
            radiusType: 'none',
          },
        },
      ],
    },
    {
      type: 'group',
      id: 'boxShadowGroup',
      controls: [
        {
          id: 'hasBoxShadow',
          type: 'toggle',
          label: 'Shadow',
          option: {
            label: 'large',
          },
          simple: true,
          default: false,
          links: [
            {
              value: true,
              control: {
                ids: ['boxShadow'],
                show: true,
              },
            },
          ],
        },
        {
          id: 'boxShadow',
          type: 'boxShadow',
          default: {
            ...fixedPresetConfig.shadow['shadow-1'],
          },
        },
      ],
    },
    {
      type: 'group',
      controls: [
        {
          id: 'op',
          label: 'Opacity',
          option: {
            label: 'large',
          },
          type: 'range',
          min: 0,
          max: 100,
          step: 1,
          units: ['%'],
          devices: {
            desktop: {
              default: '100%',
            },
            tablet: {},
            mobile: {},
          },
        },
      ],
    },
    {
      id: 'position-group',
      type: 'group',
      controls: [
        {
          id: 'pos',
          type: 'select',
          label: 'Position',
          option: {
            label: 'large',
          },
          options: [
            {
              value: 'static',
              label: 'Static',
            },
            {
              value: 'relative',
              label: 'Relative',
            },
            {
              value: 'absolute',
              label: 'Absolute',
            },
            {
              value: 'sticky',
              label: 'Sticky',
            },
            {
              value: 'fixed',
              label: 'Fixed',
            },
          ],
          links: [
            {
              value: 'fixed',
              control: {
                ids: ['position-spacing-group'],
                show: true,
              },
            },
            {
              value: 'sticky',
              control: {
                ids: ['position-spacing-group'],
                show: true,
              },
            },
            {
              value: 'relative',
              control: {
                ids: ['position-spacing-group'],
                show: true,
              },
            },
            {
              value: 'absolute',
              control: {
                ids: ['position-spacing-group'],
                show: true,
              },
            },
          ],
          devices: {
            desktop: 'static',
            tablet: 'static',
            mobile: 'static',
          },
        },
        {
          id: 'position-spacing-group',
          type: 'group',
          label: 'Spacing',
          option: {
            label: 'large',
          },
          hide: true,
          controls: [
            {
              id: 'top',
              label: 'Top',
              option: {
                label: 'large',
              },
              type: 'input:unit',
              units: ['px', '%'],
              devices: {
                desktop: '0%',
                tablet: '0%',
                mobile: '0%',
              },
            },
            {
              id: 'right',
              label: 'Right',
              type: 'input:unit',
              units: ['px', '%'],
              devices: {
                desktop: '0%',
                tablet: '0%',
                mobile: '0%',
              },
            },
            {
              id: 'bottom',
              label: 'Bottom',
              type: 'input:unit',
              units: ['px', '%'],
              devices: {
                desktop: '0%',
                tablet: '0%',
                mobile: '0%',
              },
            },
            {
              id: 'left',
              label: 'Left',
              type: 'input:unit',
              units: ['px', '%'],
              devices: {
                desktop: '0%',
                tablet: '0%',
                mobile: '0%',
              },
            },
            {
              id: 'z',
              label: 'Z-Index',
              type: 'input:number',
              devices: {
                desktop: 0,
                tablet: 0,
                mobile: 0,
              },
            },
          ],
        },
      ],
    },
  ],
};

export const advancedSettingUI: ControlUI[] = [
  {
    type: 'group',
    label: {
      en: 'Visibility',
    },
    controls: [
      {
        type: 'control',
        layout: 'vertical',
        setting: {
          id: 'd',
        },
      },
    ],
  },
  {
    type: 'group',
    label: {
      en: 'Spacing (px)',
    },
    controls: [
      {
        type: 'control',
        layout: 'vertical',
        setting: {
          id: 'spacing-setting',
        },
      },
    ],
  },
  {
    type: 'control',
    label: {
      en: 'Opacity',
    },
    setting: {
      id: 'op',
    },
  },
  {
    type: 'group',
    label: {
      en: 'Effect',
    },
    controls: [
      {
        type: 'control',
        label: {
          en: 'Border',
        },
        setting: {
          id: 'border',
        },
      },
      {
        type: 'control',
        label: {
          en: 'Corner',
        },
        setting: {
          id: 'rounded',
        },
      },
      {
        type: 'control',
        label: {
          en: 'Shadow',
        },
        setting: {
          id: 'hasBoxShadow',
        },
      },
      {
        type: 'control',
        condition: 'hasBoxShadow == true',
        layout: 'vertical',
        setting: {
          id: 'boxShadow',
        },
      },
    ],
  },
  {
    type: 'group',
    label: {
      en: '',
    },
    controls: [
      {
        type: 'control',
        label: {
          en: 'Position',
        },
        setting: {
          id: 'pos',
        },
        options: {
          label: 'large',
        },
      },
      {
        type: 'control',
        condition: 'pos && pos !== "static"',
        label: {
          en: 'Top',
        },
        setting: {
          id: 'top',
        },
      },
      {
        type: 'control',
        condition: 'pos && pos !== "static"',
        label: {
          en: 'Right',
        },
        setting: {
          id: 'right',
        },
      },
      {
        type: 'control',
        condition: 'pos && pos !== "static"',
        label: {
          en: 'Bottom',
        },
        setting: {
          id: 'bottom',
        },
      },
      {
        type: 'control',
        condition: 'pos && pos !== "static"',
        label: {
          en: 'Left',
        },
        setting: {
          id: 'left',
        },
      },
      {
        type: 'control',
        condition: 'pos && pos !== "static"',
        label: {
          en: 'Z-Index',
        },
        setting: {
          id: 'z',
        },
      },
    ],
  },
];

export const domainRegex =
  // eslint-disable-next-line no-useless-escape
  /^(((?!\-))(xn\-\-)?[a-z0-9\-_]{0,61}[a-z0-9]{1,1}\.)*(xn\-\-)?([a-z0-9\-]{1,61}|[a-z0-9\-]{1,30})\.[a-z]{2,}$/;

export const MAXIMUM_SECTION = 25;

export const LIMIT_DISPLAY_SECTION_WARNING = 21;
export const DELAY_TIMEOUT = 30;
export const VIEW_PORT_OFFSET = 8;
export const OFFSET_WITH_SELECTION = 8;

export const HIGH_PRIORITY_PARENT_COMPONENT_TAG = ['Product', 'ProductList'];
